import { GantWP } from './GantWP';
import { tokens } from '../../../global/theme/tokens';
import { WorkPackageForm } from '../project/workPackage/WorkPackageForm';
import { createElement, Fragment, useState } from 'react';
import { updateOrCreate } from '../../special/updateOrCreate';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { GantPopper } from './GantPopper';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import FlagIcon from '@mui/icons-material/Flag';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import DateLabel from '../../label/DateLabel';
import { useTimeTracking } from '../../time-tracking/AppTimeProvider';
import { ProjectUserStack } from '../project/ProjectUserStack';
import SmallProgressBar from '../../special/SmallProgressBar';
import { renderHours } from '../../../scenes/time-tracking/worktime';

import { styled } from '@mui/material/styles';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import moment from 'moment';
export const GantPhase = ({
  phase,
  isFirst,
  dayWidth,
  onChange,
  days,
  inEditMode,
  projectId,
  resolution,
  allowedStatuses,
  editable,
  infoWidth = 0, // Width for additional info column
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  const { name, workPackages } = phase;
  const [showPackages, setShowPackages] = useState(true);

  const [showWP, setShowWP] = useState(null);
  const [editWP, setEditWP] = useState(null);

  const [showWpInfo, setShowWpInfo] = useState(false);
  const [expandedWpId, setExpandedWpId] = useState(null);

  function handleUpdateWorkPackage(updatedWP) {
    const newWorkPackages = updateOrCreate(workPackages, updatedWP);
    onChange({ ...phase, workPackages: newWorkPackages });
  }

  const handleClick = (event, wp) => {
    const clickX = event.clientX;
    const clickY = event.clientY;
    const virtualAnchor = {
      getBoundingClientRect: () => ({
        top: clickY,
        left: clickX,
        bottom: clickY,
        right: clickX,
        width: 0,
        height: 0,
        x: clickX,
        y: clickY,
        toJSON: () => {},
      }),
    };

    setShowWP({ wp: wp, anchor: virtualAnchor }); //event.target });
  };

  const statusMap = {
    0: {
      label: t('Running'),
      Icon: DirectionsRunIcon,
      color: colors.greenAccent[600],
      opacity: 1,
    },
    1: {
      label: t('Finished'),
      Icon: FlagIcon,
      color: colors.grey[600],
      opacity: 1,
    },
    2: {
      label: t('Infinite'),
      Icon: AllInclusiveIcon,
      color: colors.blueAccent[600],
      opacity: 1,
    },
    3: {
      label: t('Canceled'),
      Icon: ReportGmailerrorredIcon,
      color: colors.redAccent[600],
      opacity: 1,
    },
  };

  const { clockIn, clockOut, activeProject, secondsIn } = useTimeTracking();

  const sortedWorkPackages = [...workPackages].sort((a, b) => {
    if (!a.startDate) return 1;
    if (!b.startDate) return -1;

    const dateA = moment(a.startDate);
    const dateB = moment(b.startDate);

    if (dateA.isBefore(dateB)) return -1;
    if (dateA.isAfter(dateB)) return 1;
    return 0;
  });

  return (
    <Fragment>
      <tr className="table-row lg:hidden ">
        <td colSpan={999} className="h-[65px]"></td>
      </tr>
      <tr className="hidden lg:table-row">
        <td
          style={{
            backgroundColor: colors.glass,
          }}
          className=" sticky left-0 backdrop-blur-sm hidden lg:table-cell"
        >
          <Box className="pb-1">
            <Typography
              fontWeight={600}
              className="pl-2 pt-[1px] cursor-pointer"
              onClick={() => setShowPackages(!showPackages)}
            >
              <ArrowDropDownIcon
                sx={{
                  transform: `rotate(${showPackages ? 180 : 0}deg)`,
                }}
                className="mr-1"
              />
              {name}
            </Typography>

            <WorkPackageForm
              phaseId={phase.id}
              open={Boolean(editWP)}
              onClose={() => setEditWP(null)}
              startDataCollection={editWP}
              onChange={handleUpdateWorkPackage}
            />
            <GantPopper
              open={Boolean(showWP)}
              anchorEl={showWP?.anchor}
              workPackage={showWP?.wp}
              projectId={projectId}
              onClose={() => setShowWP(null)}
              inEditMode={inEditMode}
              onEdit={setEditWP}
            />
          </Box>
        </td>
        <td
          style={{ backgroundColor: colors.glass }}
          className="min-w-[10px] w-[10px] hidden lg:table-cell"
        ></td>
        <td></td>
      </tr>

      {sortedWorkPackages?.map((workPackage) => {
        const activeWorkpackage =
          activeProject &&
          activeProject.activeWorkPackage &&
          activeProject.activeWorkPackage.id === workPackage.id
            ? {
                ...activeProject.activeWorkPackage,
                totalTimeWorkedSeconds: secondsIn
                  ? activeProject.activeWorkPackage.totalTimeWorkedSeconds +
                    secondsIn
                  : activeProject.activeWorkPackage.totalTimeWorkedSeconds,
              }
            : null;

        const workPercentage = activeWorkpackage
          ? activeWorkpackage.totalTimeWorkedSeconds /
            activeWorkpackage.estimatedTime
          : workPackage.totalTimeWorkedSeconds / workPackage.estimatedTime;

        const workedTime =
          (activeWorkpackage
            ? activeWorkpackage.totalTimeWorkedSeconds
            : workPackage.totalTimeWorkedSeconds) / 60;

        return (
          <tr
            className={showPackages ? 'table-row' : 'hidden'}
            key={workPackage.id}
          >
            <td
              style={{
                width: infoWidth,
                minWidth: infoWidth,
                backgroundColor: colors.glass,
              }}
              className=" sticky left-0 z-10 backdrop-blur-sm hidden lg:table-cell"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  paddingLeft: 1,
                  minHeight: 45,
                }}
              >
                <Box
                  component="img"
                  src={workPackage.icon?.temporaryUrl}
                  alt={`${workPackage.title} icon`}
                  sx={{
                    width: 40,
                    height: 45,
                    objectFit: 'contain',
                    flexShrink: 0,
                    alignSelf: 'flex-start', // change this line
                  }}
                />

                <Box
                  sx={{
                    maxWidth: 250,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 48,
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      setExpandedWpId(
                        expandedWpId === workPackage.id ? null : workPackage.id
                      )
                    }
                  >
                    {createElement(statusMap[workPackage.status].Icon, {
                      sx: {
                        color: statusMap[workPackage.status]?.color,
                        mr: 0.5,
                        flexShrink: 0,
                      },
                    })}
                    <Tooltip title={workPackage.title} arrow>
                      <Typography
                        fontWeight={600}
                        noWrap
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          flexGrow: 1,
                        }}
                      >
                        {workPackage.title}
                      </Typography>
                      {Boolean(workPercentage) &&
                        workPercentage !== Infinity && (
                          <Box sx={{}}>
                            <BorderLinearProgress
                              variant="determinate"
                              value={Math.min(
                                Math.round(workPercentage * 100),
                                100
                              )}
                            />
                            <Typography
                              variant="caption"
                              sx={{ mt: 0.5 }}
                              color="text.secondary"
                            >
                              {Math.round(workPercentage * 100)}% (
                              {renderHours(workedTime)}h
                              {workPackage.estimatedTime
                                ? ` / ${renderHours(
                                    workPackage.estimatedTime / 60
                                  )}h`
                                : ''}
                              )
                            </Typography>
                          </Box>
                        )}
                    </Tooltip>
                    <ArrowDropDownIcon
                      sx={{
                        ml: 0.5,
                        transform: `rotate(${
                          expandedWpId === workPackage.id ? 180 : 0
                        }deg)`,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Box>

                  {expandedWpId === workPackage.id && (
                    <Box
                      className="gap-2 flex flex-col items-start pl-6 pb-2"
                      sx={{
                        '.MuiAvatar-root': {
                          width: '30px',
                          height: '30px',
                        },
                      }}
                    >
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        className="opacity-60"
                        sx={{ textAlign: 'left' }} // center text horizontally
                      >
                        {workPackage.startDate ? (
                          <DateLabel date={workPackage.startDate} />
                        ) : (
                          '??'
                        )}
                        {' - '}
                        {workPackage.endDate ? (
                          <DateLabel date={workPackage.endDate} />
                        ) : (
                          '??'
                        )}
                      </Typography>
                      <Box className="team cursor-pointer">
                        <ProjectUserStack
                          editmode={inEditMode}
                          users={workPackage.users}
                          workPackage={workPackage}
                          onChange={handleUpdateWorkPackage}
                          projectId={projectId}
                          noLabel
                          size="20px"
                          noEmptyIcon
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </td>
            <td
              className="min-w-[10px] hidden lg:table-cell"
              style={{ backgroundColor: colors.glass }}
            ></td>
            <td className="h-[55px]">
              <GantWP
                key={workPackage.id}
                workPackage={workPackage}
                dayWidth={dayWidth}
                onEdit={(data) => {
                  setEditWP(data);
                  setShowWP(null);
                }}
                onShow={handleClick}
                days={days}
                inEditMode={inEditMode}
                projectId={projectId}
                onChange={handleUpdateWorkPackage}
                resolution={resolution}
                allowedStatuses={allowedStatuses}
                editable={editable}
                infoWidth={infoWidth}
              />
            </td>
          </tr>
        );
      })}
    </Fragment>
  );
};

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300],
    ...theme.applyStyles?.('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles?.('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));
